export const escapeXml = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export type Measure = (text: string) => number;
type MeasurerFactory = (fontSize: number, fontWeight: number) => Measure;

export const createCanvasMeasurer =
  (fontFamily: string): MeasurerFactory =>
  (fontSize, fontWeight) => {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    return (text) => ctx.measureText(text).width;
  };

type WrapResult = { lines: string[]; fits: boolean };

// Splits a single word at the character level into chunks that each fit within `maxWidth`.
// Used for inputs like a long unbroken name where word-level wrapping alone would overflow
// the line. Iterates by code point (Unicode-safe).
export const splitWordIntoChunks = (word: string, measure: Measure, maxWidth: number): string[] => {
  const chunks: string[] = [];
  let current = "";
  for (const char of word) {
    const candidate = current + char;
    if (!current || measure(candidate) <= maxWidth) {
      current = candidate;
    } else {
      chunks.push(current);
      current = char;
    }
  }
  if (current) chunks.push(current);
  return chunks;
};

export const wrapToLines = (
  text: string,
  measure: Measure,
  maxWidth: number,
  maxLines: number,
  breakLongWords = false
): WrapResult => {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return { lines: [""], fits: true };

  const allLines: string[] = [];
  let current = "";
  for (const word of words) {
    if (breakLongWords && measure(word) > maxWidth) {
      // Word can't fit on a line on its own — flush whatever's on the current line, break
      // the word into character-level chunks, push all but the last, and let the loop
      // continue with the last chunk as the new working line so any following word can
      // still append to it.
      if (current) allLines.push(current);
      const chunks = splitWordIntoChunks(word, measure, maxWidth);
      for (let i = 0; i < chunks.length - 1; i++) allLines.push(chunks[i]);
      current = chunks[chunks.length - 1] ?? "";
      continue;
    }

    const candidate = current ? `${current} ${word}` : word;
    if (!current || measure(candidate) <= maxWidth) {
      current = candidate;
    } else {
      allLines.push(current);
      current = word;
    }
  }
  if (current) allLines.push(current);

  const anyLineTooWide = allLines.some((line) => measure(line) > maxWidth);
  const tooManyLines = allLines.length > maxLines;

  if (!tooManyLines) {
    return { lines: allLines, fits: !anyLineTooWide };
  }

  return { lines: allLines.slice(0, maxLines), fits: false };
};

// Auto-shrinks the font until the wrapped text fits within `maxLines` and `maxWidth`.
// Falls back to the smallest tried size if nothing fits
export const fitText = (
  text: string,
  baseFontSize: number,
  fontWeight: number,
  maxWidth: number,
  maxLines: number,
  measurerFactory: MeasurerFactory,
  minFontScale: number,
  breakLongWords = false
) => {
  const minFontSize = Math.max(1, Math.floor(baseFontSize * minFontScale));
  for (let fontSize = baseFontSize; fontSize >= minFontSize; fontSize -= 1) {
    const result = wrapToLines(
      text,
      measurerFactory(fontSize, fontWeight),
      maxWidth,
      maxLines,
      breakLongWords
    );
    if (result.fits) return { lines: result.lines, fontSize };
  }

  const fallback = wrapToLines(
    text,
    measurerFactory(minFontSize, fontWeight),
    maxWidth,
    maxLines,
    breakLongWords
  );

  return { lines: fallback.lines, fontSize: minFontSize };
};
